export const ADDED_BY_YOU_KEY = "__added";
export const PREVENTIVE_CARE_KEY = "__preventive";

export type AgendaGroupableItem = {
  id: number;
  careProtocolOpportunityId?: number | null;
  /** Prefer nested `healthConcern.id` from enriched list payloads. */
  healthConcernId?: number | null;
  healthConcern?: { id: number } | null;
};

export type AgendaConcernGroup<T extends AgendaGroupableItem> = {
  key: string;
  healthConcernId: number | null;
  items: T[];
};

function isVirtualConcernGroupKey(key: string): boolean {
  return key === ADDED_BY_YOU_KEY || key === PREVENTIVE_CARE_KEY;
}

/**
 * Resolve the concern bucket for an agenda item.
 *
 * Mirrors the server's enrichment precedence (see
 * `agenda-items.enrichment.ts`): the nested `healthConcern` — populated from
 * the opportunity's concern when the item is opportunity-linked, or from the
 * item's own direct `healthConcernId` otherwise — is preferred whenever
 * present, which already encodes "opportunity wins": enrichment resolves an
 * opportunity-linked item's concern through its opportunity first, so the
 * nested field always reflects that resolution before a direct association
 * would ever be consulted server-side. The flat `healthConcernId` is only a
 * fallback for callers that haven't gone through enrichment (e.g. direct
 * fixtures in tests).
 *
 * Items with a concern (including custom items assigned via STITCH-550 drag)
 * land in that concern group. Opportunity items without a concern land in
 * Preventive Care. Custom items without a concern land in Added by you.
 */
export function concernGroupKey(item: AgendaGroupableItem): string {
  const healthConcernId = item.healthConcern?.id ?? item.healthConcernId;
  if (healthConcernId != null) return String(healthConcernId);
  if (item.careProtocolOpportunityId != null) return PREVENTIVE_CARE_KEY;
  return ADDED_BY_YOU_KEY;
}

/**
 * Group flat agenda items by health concern.
 * Group order follows first appearance / contiguous blocks in the flat list.
 */
export function groupByConcern<T extends AgendaGroupableItem>(
  items: readonly T[],
): AgendaConcernGroup<T>[] {
  const buckets = new Map<string, T[]>();
  const order: string[] = [];

  for (const item of items) {
    const key = concernGroupKey(item);
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)?.push(item);
  }

  return order.map((key) => ({
    key,
    healthConcernId: isVirtualConcernGroupKey(key) ? null : Number(key),
    items: buckets.get(key) ?? [],
  }));
}

/**
 * Flatten concern groups back to a single ordered item list.
 */
export function flattenConcernGroups<T extends AgendaGroupableItem>(
  groups: readonly AgendaConcernGroup<T>[],
): T[] {
  return groups.flatMap((group) => group.items);
}

/**
 * Whether any item can be dragged back into Added by you.
 *
 * Custom items (no opportunity link) that currently sit under a health
 * concern need an empty Added by you bucket as a drop target — otherwise
 * moving the last unassigned custom item into a concern permanently
 * removes the only slot that could reverse the move.
 */
export function hasReturnableCustomItem(
  items: readonly AgendaGroupableItem[],
): boolean {
  return items.some(
    (item) =>
      item.careProtocolOpportunityId == null &&
      (item.healthConcern?.id ?? item.healthConcernId) != null,
  );
}

/**
 * Pin an empty Added by you group when custom items could return to it.
 *
 * `groupByConcern` only emits buckets that already have members, so the last
 * custom item leaving Added by you would otherwise delete the drop target.
 */
export function ensureDroppableAddedByYouGroup<T extends AgendaGroupableItem>(
  groups: readonly AgendaConcernGroup<T>[],
  items: readonly T[],
): AgendaConcernGroup<T>[] {
  if (groups.some((group) => group.key === ADDED_BY_YOU_KEY)) {
    return [...groups];
  }
  if (!hasReturnableCustomItem(items)) return [...groups];
  return [
    ...groups,
    { key: ADDED_BY_YOU_KEY, healthConcernId: null, items: [] },
  ];
}

/**
 * Reorder items within a single concern group. Returns null when the move is
 * cross-group or either id is missing.
 */
export function reorderItemsWithinGroup<T extends AgendaGroupableItem>(
  items: readonly T[],
  draggedId: number,
  overId: number,
): T[] | null {
  if (draggedId === overId) return null;

  const fromIndex = items.findIndex((item) => item.id === draggedId);
  const toIndex = items.findIndex((item) => item.id === overId);
  if (fromIndex < 0 || toIndex < 0) return null;

  const fromKey = concernGroupKey(items[fromIndex]!);
  const toKey = concernGroupKey(items[toIndex]!);
  if (fromKey !== toKey) return null;

  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return null;
  next.splice(toIndex, 0, moved);
  return next;
}

/**
 * Move an entire concern block so it appears before the target group.
 * Rewrites the flat item order; concern order is derived from that list.
 */
export function reorderConcernGroups<T extends AgendaGroupableItem>(
  items: readonly T[],
  fromKey: string,
  toKey: string,
): T[] | null {
  if (fromKey === toKey) return null;

  const groups = groupByConcern(items);
  const order = groups
    .map((group) => group.key)
    .filter((key) => key !== fromKey);
  const at = order.indexOf(toKey);
  if (at < 0) return null;
  if (!groups.some((group) => group.key === fromKey)) return null;

  order.splice(at, 0, fromKey);
  const byKey = new Map(groups.map((group) => [group.key, group.items]));
  return order.flatMap((key) => byKey.get(key) ?? []);
}
